/**
 * StaffSubscriptionPlans — Verwaltung der ERPNext-Subscription-Plans (Pricing-Katalog).
 *
 * TQ #9695 (Staff-Migration Teil B), portiert von
 * customer-portal/src/pages/staff/StaffSubscriptionPlans.tsx (dort migriert
 * von admin-portal/src/pages/SubscriptionPlansCatalog/).
 *
 * WICHTIG (workspace-catalog.md): ERPNext ist Single Point of Truth fuer
 * Preise. Diese Seite AENDERT KEINE Preise (`rate`/`currency` sind reine
 * Anzeige) — nur Sichtbarkeit (is_catalog_visible), Cockpit-Anzeigename/
 * -Reihenfolge (variant_name/variant_description/display_order) und die
 * Item-Zuordnung (item_code, Katalog-Gruppierung). Preis-Editierung bleibt
 * ERPNext vorbehalten.
 *
 * Workflows:
 *   - Liste mit Suche (Name/Item/ERPNext-ID) + Item-Group-Filter + Visibility-Filter
 *   - Inline-Toggle: is_catalog_visible je Plan
 *   - Detail-Drawer (rechts): Stammdaten, Variant-Felder, Item-Reference
 *   - "Import" triggert ERPNext-Sync und refetcht
 *   - Kompakte Stats-Card oben (Anzahl visible/hidden/active)
 */

import { useMemo, useState } from 'react'
import {
  PageHeader,
  DataTable,
  ConfirmDialog,
  useApiResource,
  useApiMutation,
  type DataTableFilterOption,
} from '@it-sce/ui'
import {
  type TSubscriptionPlan,
  type TSubscriptionPlanVariantRequest,
} from '@it-sce/ui'
import { useStaffApi } from '../hooks/useStaffApi'
import { useToast } from '../components/ui'

type VisibilityFilter = 'all' | 'visible' | 'hidden'

const VIS_OPTIONS: DataTableFilterOption[] = [
  { value: 'all', label: 'Alle' },
  { value: 'visible', label: 'Sichtbar' },
  { value: 'hidden', label: 'Versteckt' },
]

function formatPrice(p: TSubscriptionPlan): string {
  return `${p.rate.toFixed(2)} ${p.currency} / ${p.billing_interval}`
}

function formatDate(iso?: string | null): string {
  if (!iso) return '–'
  return new Date(iso).toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

function ToggleSwitch({
  checked, onChange, disabled = false, label,
}: {
  checked: boolean
  onChange: (next: boolean) => void
  disabled?: boolean
  label?: string
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={(e) => { e.stopPropagation(); onChange(!checked) }}
      style={{
        width: 36, height: 20, borderRadius: 10, border: 'none',
        background: checked ? '#10b981' : '#d1d5db',
        position: 'relative',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'background 0.15s',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <span aria-hidden style={{
        position: 'absolute', top: 2, left: checked ? 18 : 2,
        width: 16, height: 16, borderRadius: '50%', background: '#fff',
        transition: 'left 0.15s',
      }} />
    </button>
  )
}

export default function StaffSubscriptionPlans() {
  const staff = useStaffApi()
  const { showToast } = useToast()

  const [search, setSearch] = useState('')
  const [itemFilter, setItemFilter] = useState<string>('all')
  const [visFilter, setVisFilter] = useState<VisibilityFilter>('all')
  const [selected, setSelected] = useState<TSubscriptionPlan | null>(null)
  const [confirmImport, setConfirmImport] = useState(false)

  const { data, loading, error, refetch } = useApiResource(
    ['staff-subscription-plans'],
    () => staff.subscriptionPlans.list()
  )

  const setVisibility = useApiMutation(
    ({ id, visible }: { id: number; visible: boolean }) =>
      staff.subscriptionPlans.setVisibility(id, { is_catalog_visible: visible }),
    {
      onSuccess: () => {
        showToast('success', 'Sichtbarkeit aktualisiert')
        void refetch()
      },
      onError: (e) => showToast('error', `Aktualisierung fehlgeschlagen: ${e.message}`),
    }
  )

  const importPlans = useApiMutation(
    () => staff.subscriptionPlans.triggerImport(),
    {
      onSuccess: (res) => {
        showToast('success', `Import OK · ${res.created ?? 0} neu, ${res.updated ?? 0} aktualisiert`)
        setConfirmImport(false)
        void refetch()
      },
      onError: (e) => {
        showToast('error', `Import fehlgeschlagen: ${e.message}`)
        setConfirmImport(false)
      },
    }
  )

  const plans = data?.plans ?? []

  const items = useMemo(() => {
    const set = new Set<string>()
    plans.forEach((p) => p.item_code && set.add(p.item_code))
    return Array.from(set).sort()
  }, [plans])

  const itemOptions: DataTableFilterOption[] = useMemo(
    () => [
      { value: 'all', label: 'Alle Items' },
      ...items.map((c) => ({ value: c, label: c })),
    ],
    [items]
  )

  const filterPredicate = useMemo(() => {
    const q = search.trim().toLowerCase()
    return (p: TSubscriptionPlan) => {
      if (itemFilter !== 'all' && p.item_code !== itemFilter) return false
      if (visFilter === 'visible' && !p.is_catalog_visible) return false
      if (visFilter === 'hidden' && p.is_catalog_visible) return false
      if (!q) return true
      return [p.name, p.erpnext_plan_id, p.item_code ?? '', p.variant_name ?? '', p.description ?? '']
        .join(' ').toLowerCase().includes(q)
    }
  }, [search, itemFilter, visFilter])

  const stats = useMemo(() => ({
    total: plans.length,
    visible: plans.filter((p) => p.is_catalog_visible).length,
    active: plans.filter((p) => p.is_active).length,
  }), [plans])

  return (
    <div className="page-content">
      <PageHeader
        title="Subscription-Plans"
        icon="🔁"
        subtitle={`${stats.total} Plans · ${stats.visible} im Katalog sichtbar · ${stats.active} aktiv`}
        actions={
          <button
            type="button"
            onClick={() => setConfirmImport(true)}
            disabled={importPlans.loading}
            style={{
              padding: '0.5rem 1rem', background: '#0ea5e9', color: '#fff',
              border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer',
            }}
          >
            ↻ Import von ERPNext
          </button>
        }
      />

      <DataTable<TSubscriptionPlan>
        columns={[
          {
            key: 'name', header: 'Plan', sortable: true,
            render: (p) => (
              <div>
                <div style={{ fontWeight: 600 }}>{p.variant_name || p.name}</div>
                <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                  <code>{p.erpnext_plan_id}</code>
                  {p.item_code && <> · Item: <code>{p.item_code}</code></>}
                </div>
              </div>
            ),
          },
          { key: 'item_name', header: 'Item-Gruppe', sortable: true, width: '180px',
            render: (p) => p.item_name || <span style={{ color: '#9ca3af' }}>–</span>,
          },
          { key: 'rate', header: 'Preis', align: 'right', width: '160px', sortable: true,
            render: formatPrice,
          },
          { key: 'display_order', header: 'Reihenfolge', align: 'center', width: '110px', sortable: true,
            render: (p) => p.display_order,
          },
          { key: 'is_catalog_visible', header: 'Sichtbar', align: 'center', width: '100px',
            render: (p) => (
              <ToggleSwitch
                checked={p.is_catalog_visible}
                onChange={(next) => setVisibility.mutate({ id: p.id, visible: next }).catch(() => {})}
                disabled={setVisibility.loading}
                label={`${p.name} sichtbar`}
              />
            ),
          },
          { key: 'is_active', header: 'Aktiv', align: 'center', width: '90px',
            render: (p) => (
              <span style={{
                background: p.is_active ? '#d1fae5' : '#fee2e2',
                color: p.is_active ? '#065f46' : '#991b1b',
                padding: '2px 8px', borderRadius: 4,
                fontSize: '0.75rem', fontWeight: 600,
              }}>
                {p.is_active ? 'Aktiv' : 'Inaktiv'}
              </span>
            ),
          },
          { key: 'last_sync_at', header: 'Sync', width: '140px',
            render: (p) => (
              <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                {formatDate(p.last_sync_at)}
              </div>
            ),
          },
        ]}
        data={plans}
        keyExtractor={(p) => String(p.id)}
        loading={loading}
        error={error}
        filterPredicate={filterPredicate}
        onRowClick={(p) => setSelected(p)}
        search={{
          value: search, onChange: setSearch,
          placeholder: 'Name, ERPNext-ID, Item, Variant…',
        }}
        filters={[
          { key: 'item', label: 'Item', value: itemFilter, options: itemOptions, onChange: setItemFilter },
          { key: 'visibility', label: 'Sichtbarkeit', value: visFilter, options: VIS_OPTIONS,
            onChange: (v) => setVisFilter(v as VisibilityFilter),
          },
        ]}
        onRefresh={() => void refetch()}
        emptyMessage={
          plans.length === 0
            ? 'Keine Subscription-Plans — Import von ERPNext anstoßen'
            : 'Kein Plan passt zu den Filtern'
        }
      />

      <ConfirmDialog
        isOpen={confirmImport}
        title="Plans aus ERPNext importieren?"
        variant="info"
        confirmLabel="Import starten"
        loading={importPlans.loading}
        message={
          <>
            Holt alle Subscription-Plans aus ERPNext und aktualisiert den Cache.
            Bestehende Visibility- und Variant-Einstellungen bleiben erhalten.
          </>
        }
        onConfirm={() => importPlans.mutate(undefined as unknown as void).catch(() => {})}
        onCancel={() => setConfirmImport(false)}
      />

      {selected && (
        <PlanDetailDrawer
          plan={selected}
          onClose={() => setSelected(null)}
          onSaved={() => { void refetch() }}
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Detail Drawer (Variant-Felder, Item-Reference)
// ---------------------------------------------------------------------------

function PlanDetailDrawer({
  plan, onClose, onSaved,
}: {
  plan: TSubscriptionPlan
  onClose: () => void
  onSaved: () => void
}) {
  const staff = useStaffApi()
  const { showToast } = useToast()

  const [variantName, setVariantName] = useState(plan.variant_name ?? '')
  const [variantDescription, setVariantDescription] = useState(plan.variant_description ?? '')
  const [displayOrder, setDisplayOrder] = useState<number>(plan.display_order)
  const [itemCode, setItemCode] = useState(plan.item_code ?? '')

  const saveVariant = useApiMutation(
    (req: TSubscriptionPlanVariantRequest) => staff.subscriptionPlans.setVariant(plan.id, req),
    {
      onSuccess: () => { showToast('success', 'Variant gespeichert'); onSaved() },
      onError: (e) => showToast('error', `Speichern fehlgeschlagen: ${e.message}`),
    }
  )

  const saveItem = useApiMutation(
    (req: { item_code: string }) => staff.subscriptionPlans.setItem(plan.id, req),
    {
      onSuccess: () => { showToast('success', 'Item-Zuordnung gespeichert'); onSaved() },
      onError: (e) => showToast('error', `Speichern fehlgeschlagen: ${e.message}`),
    }
  )

  const variantDirty =
    variantName !== (plan.variant_name ?? '') ||
    variantDescription !== (plan.variant_description ?? '') ||
    displayOrder !== plan.display_order

  const itemDirty = itemCode !== (plan.item_code ?? '')

  return (
    <div
      role="dialog"
      aria-label={`Plan ${plan.name}`}
      style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 480,
        background: '#fff', boxShadow: '-8px 0 24px rgba(0,0,0,0.12)',
        display: 'flex', flexDirection: 'column', zIndex: 100,
      }}
    >
      <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.25rem' }}>{plan.variant_name || plan.name}</h2>
          <p style={{ margin: '4px 0 0', fontSize: '0.8125rem', color: '#6b7280' }}>
            <code>{plan.erpnext_plan_id}</code> · {formatPrice(plan)}
          </p>
        </div>
        <button type="button" onClick={onClose} aria-label="Schließen"
          style={{ border: 'none', background: 'transparent', fontSize: '1.5rem', cursor: 'pointer', color: '#6b7280' }}>
          ×
        </button>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '1rem 1.25rem' }}>
        <section>
          <h3 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: 8 }}>Variant (Cockpit-Anzeige)</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: '0.75rem', color: '#374151', fontWeight: 600 }}>Anzeigename (Variant)</span>
              <input value={variantName} onChange={(e) => setVariantName(e.target.value)}
                placeholder={plan.name}
                style={{ padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: 6 }} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: '0.75rem', color: '#374151', fontWeight: 600 }}>Beschreibung</span>
              <textarea rows={3} value={variantDescription} onChange={(e) => setVariantDescription(e.target.value)}
                style={{ padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: 6, fontFamily: 'inherit', resize: 'vertical' }} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: '0.75rem', color: '#374151', fontWeight: 600 }}>Reihenfolge</span>
              <input type="number" value={displayOrder} onChange={(e) => setDisplayOrder(parseInt(e.target.value || '0', 10))}
                style={{ padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: 6 }} />
            </label>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button type="button"
                disabled={!variantDirty || saveVariant.loading}
                onClick={() => {
                  saveVariant.mutate({
                    variant_name: variantName,
                    variant_description: variantDescription,
                    display_order: displayOrder,
                  }).catch(() => {})
                }}
                style={{
                  padding: '0.5rem 1rem',
                  background: variantDirty ? '#3b82f6' : '#9ca3af',
                  color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600,
                  cursor: variantDirty ? 'pointer' : 'not-allowed',
                }}>
                {saveVariant.loading ? '…' : 'Variant speichern'}
              </button>
            </div>
          </div>
        </section>

        <section style={{ marginTop: '1.5rem' }}>
          <h3 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: 8 }}>Item-Zuordnung (Catalog-Gruppierung)</h3>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
              <span style={{ fontSize: '0.75rem', color: '#374151', fontWeight: 600 }}>ERPNext Item-Code</span>
              <input value={itemCode} onChange={(e) => setItemCode(e.target.value)}
                placeholder={plan.item_name ?? 'kein Item'}
                style={{ padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: 6 }} />
            </label>
            <button type="button"
              disabled={!itemDirty || saveItem.loading}
              onClick={() => saveItem.mutate({ item_code: itemCode }).catch(() => {})}
              style={{
                padding: '0.5rem 1rem',
                background: itemDirty ? '#3b82f6' : '#9ca3af',
                color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600,
                cursor: itemDirty ? 'pointer' : 'not-allowed',
              }}>
              {saveItem.loading ? '…' : 'Speichern'}
            </button>
          </div>
        </section>

        <section style={{ marginTop: '1.5rem' }}>
          <h3 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: 8 }}>Sync-Status</h3>
          <dl style={{ margin: 0, fontSize: '0.8125rem', display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '0.25rem 1rem' }}>
            <dt style={{ color: '#6b7280' }}>Status</dt><dd style={{ margin: 0 }}>{plan.is_active ? 'Aktiv' : 'Inaktiv'}</dd>
            <dt style={{ color: '#6b7280' }}>Sichtbar im Katalog</dt><dd style={{ margin: 0 }}>{plan.is_catalog_visible ? 'Ja' : 'Nein'}</dd>
            <dt style={{ color: '#6b7280' }}>Letzter Sync</dt><dd style={{ margin: 0 }}>{formatDate(plan.last_sync_at)}</dd>
            <dt style={{ color: '#6b7280' }}>Erstellt</dt><dd style={{ margin: 0 }}>{formatDate(plan.created_at)}</dd>
            <dt style={{ color: '#6b7280' }}>Geändert</dt><dd style={{ margin: 0 }}>{formatDate(plan.updated_at)}</dd>
          </dl>
        </section>
      </div>
    </div>
  )
}
